import { Grid } from '@material-ui/core';
import { useQuoteField } from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import { FormRowGrid, GridRowProps } from 'Quoting/components/shared/GridRow';
import { ConditionalSection, QuoteFormConditionalSection } from 'Quoting/components/shared/QuoteFormConditionalSection';
import { QuoteFormControlElement } from 'Quoting/components/shared/QuoteFormControlElement';
import { QuoteFormElement } from 'Quoting/components/shared/QuoteFormElement';
import { FormWrapper } from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import { Map as ImmutableMap, fromJS } from 'immutable';
import { useSelector } from 'react-redux';
import { ReducerState } from 'rootReducer';
import { unitsOptions } from '../../PlanTemplates/ACC/benefitDefaults';
import { formatCurrencyValue } from './ACCBenefits';
import { getTreatmentSupportAndRecovery } from './PropertyPaths/getTreatmentSupportAndRecovery';
import { isDefined } from 'Quoting/utils/utilities';
import { createACCBenefitStructure } from '../../PlanTemplates/ACC/GAI7';

type ObjectInfo = {
  benefitAppliedPath: string,
  value: boolean,
  benefitDetailsPath: string,
  benefitDetailsObjectName: string
}

const getBenefitDetailsObject = (
  benefitDetailsObjectName: string,
  defaultValues: ImmutableMap<string, any> | undefined,
  benefitStructureFn: (...args: any[]) => Record<string, any>,
  planRootId?: string,
  coverageDefParentId?: string
) => {
  const benefitStructure: Record<string, any> = benefitStructureFn(
    defaultValues ?? ImmutableMap<string, any>(),
    planRootId ?? '',
    coverageDefParentId ?? ''
  );
  return benefitStructure[benefitDetailsObjectName];
};

const CompanionNonLocalLodging = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);

  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'companion-nonLocal-lodging-row-1' },
      items: [
        {
          key: propertyPaths.companionNonLocalLodgingDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.incurralPeriod)}
              disabled
              required
              type="number"
              id={propertyPaths.companionNonLocalLodgingDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.companionNonLocalLodgingDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Distance to the Hospital More than (Miles)"
              searchKey={`ACCCompanionNonLocalLodgingBenefit.distanceToHospitalMoreThanMilesCd`}
              value={getQuoteFieldValue<number>(
                propertyPaths.companionNonLocalLodgingDetails.distanceToHospitalMoreThanMilesCd
              )}
              disabled
              required
              id={propertyPaths.companionNonLocalLodgingDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'companion-nonLocal-lodging-row-2' },
      items: [
        {
          key: propertyPaths.companionNonLocalLodgingDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.benefitAmount)
              )}
              disabled
              required
              type="number"
              id={propertyPaths.companionNonLocalLodgingDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.companionNonLocalLodgingDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Number of Days Per Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.maxBenefitNumber)}
              disabled
              required
              type="number"
              id={propertyPaths.companionNonLocalLodgingDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'companion-nonLocal-lodging-row-3' },
      items: [
        {
          key: propertyPaths.companionNonLocalLodgingDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCCompanionNonLocalLodgingBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.companionNonLocalLodgingDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const MedicalEquipment = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'medical-equipment-row-1' },
      items: [
        {
          key: propertyPaths.medicalEquipmentDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.incurralPeriod)}
              disabled
              required
              type="number"
              id={propertyPaths.medicalEquipmentDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.medicalEquipmentDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.benefitAmount)
              )}
              disabled
              type="number"
              id={propertyPaths.medicalEquipmentDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'medical-equipment-row-2' },
      items: [
        {
          key: propertyPaths.medicalEquipmentDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMedicalEquipmentBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.medicalEquipmentDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.medicalEquipmentDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              required
              searchKey={`ACCMedicalEquipmentBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.timePeriodCd)}
              disabled
              id={propertyPaths.medicalEquipmentDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const MedicalSupplies = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'medical-supplies-row-1' },
      items: [
        {
          key: propertyPaths.medicalSuppliesDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.incurralPeriod)}
              disabled
              required
              type="number"
              id={propertyPaths.medicalSuppliesDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.medicalSuppliesDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.benefitAmount)
              )}
              disabled
              required
              type="number"
              id={propertyPaths.medicalSuppliesDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'medical-supplies-row-2' },
      items: [
        {
          key: propertyPaths.medicalSuppliesDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMedicalSuppliesBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.medicalSuppliesDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.medicalSuppliesDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCMedicalSuppliesBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.medicalSuppliesDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Medication = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'medication-row-1' },
      items: [
        {
          key: propertyPaths.medicationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.medicationDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.medicationDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.medicationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.medicationDetails.benefitAmount))}
              disabled
              required
              id={propertyPaths.medicationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'medication-row-2' },
      items: [
        {
          key: propertyPaths.medicationDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMedicationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.medicationDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.medicationDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.medicationDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCMedicationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.medicationDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.medicationDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const NonLocalTransportation = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'non-local-transportation-row-1' },
      items: [
        {
          key: propertyPaths.nonLocalTransportationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.nonLocalTransportationDetails.travelDistanceMoreThan,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Travel Distance More Than (Miles - One Way)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.travelDistanceMoreThan)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.travelDistanceMoreThan}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'non-local-transportation-2' },
      items: [
        {
          key: propertyPaths.nonLocalTransportationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Round Trip)"
              type="number"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.benefitAmount)
              )}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.nonLocalTransportationDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCNonLocalTransportationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'non-local-transportation-3' },
      items: [
        {
          key: propertyPaths.nonLocalTransportationDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCNonLocalTransportationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const PostAccidentCommonCarrierTransportation = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);

  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'post-accident-common-carrier-transportation-row-1' },
      items: [
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              required
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.incurralPeriod
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.travelDistanceMoreThan,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Travel Distance More Than (Miles - One Way)"
              type="number"
              required
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.travelDistanceMoreThan
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.travelDistanceMoreThan}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'post-accident-common-carrier-transportation-row-2' },
      items: [
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.postAccidentCommonCarrierTransportationDetails.benefitAmount)
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCPostAccidentCommonCarrierTransportationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.maxBenefitNumber
              )}
              disabled
              required
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'post-accident-common-carrier-transportation-row-3' },
      items: [
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              required
              searchKey={`ACCPostAccidentCommonCarrierTransportationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.timePeriodCd
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Prosthesis = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'prosthesis-row-1' },
      items: [
        {
          key: propertyPaths.prosthesisDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.prosthesisDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.prosthesisDetails.oneDeviceAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="One Device"
              type="number"
              required
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.oneDeviceAmount))}
              disabled
              id={propertyPaths.prosthesisDetails.oneDeviceAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'prosthesis-row-2' },
      items: [
        {
          key: propertyPaths.prosthesisDetails.twoOrMoreDevicesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or More Devices"
              type="number"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.twoOrMoreDevicesAmount)
              )}
              disabled
              id={propertyPaths.prosthesisDetails.twoOrMoreDevicesAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.prosthesisDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCProsthesisBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.prosthesisDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'prosthesis-row-3' },
      items: [
        {
          key: propertyPaths.prosthesisDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              required
              searchKey={`ACCProsthesisBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.timePeriodCd)}
              disabled
              id={propertyPaths.prosthesisDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const ResidenceOrAutomobileModification = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'residence-automobile-modification-row-1' },
      items: [
        {
          key: propertyPaths.residenceAutomobileModificationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.residenceAutomobileModificationDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.residenceAutomobileModificationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.benefitAmount)
              )}
              disabled
              id={propertyPaths.residenceAutomobileModificationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'residence-automobile-modification-row-2' },
      items: [
        {
          key: propertyPaths.residenceAutomobileModificationDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCResidenceAutomobileModificationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.residenceAutomobileModificationDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.residenceAutomobileModificationDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCResidenceAutomobileModificationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.residenceAutomobileModificationDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Therapy = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'therapy-row-1' },
      items: [
        {
          key: propertyPaths.therapyDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.therapyDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.therapyDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.therapyDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              type="number"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.therapyDetails.benefitAmount))}
              disabled
              required
              id={propertyPaths.therapyDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'therapy-row-2' },
      items: [
        {
          key: propertyPaths.therapyDetails.isAlternateTherapyApplied,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              id={propertyPaths.therapyDetails.isAlternateTherapyApplied}
              value={getQuoteFieldValue<boolean>(propertyPaths.therapyDetails.isAlternateTherapyApplied)}
              disabled
              label="Alternate Therapies (incl. Acupuncture)"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.therapyDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCTherapyBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.therapyDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.therapyDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'therapy-row-3' },
      items: [
        {
          key: propertyPaths.therapyDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCTherapyBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.therapyDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.therapyDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const SpinalManipulationServicesDetails = () => {
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'spinal-manipulation-row-1' },
      items: [
        {
          key: propertyPaths.spinalManipulationServicesDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.spinalManipulationServicesDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.spinalManipulationServicesDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.spinalManipulationServicesDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              type="number"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.spinalManipulationServicesDetails.benefitAmount))}
              disabled
              required
              id={propertyPaths.spinalManipulationServicesDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'spinal-manipulation-row-1' },
      items: [
        {
          key: propertyPaths.spinalManipulationServicesDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Days Benefit is Payable per Time Period"
              searchKey={`ACCTherapyBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.spinalManipulationServicesDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.spinalManipulationServicesDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: propertyPaths.spinalManipulationServicesDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCTherapyBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.spinalManipulationServicesDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.spinalManipulationServicesDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const TreatmentSupportAndRecoveryBlock = () => {
  const { selectedPlan, defaultPlanValues } = useSelector((state: ReducerState) => state.quoteForm);
  const { getQuoteFieldValue, updateQuoteFieldValues } = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);

  const planRootId = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.rootId`);
  const coverageDefId = getQuoteFieldValue(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[0]._key.rootId`
  );
  const situsStateCd = getQuoteFieldValue('riskStateCd') ?? '';

  const onToggleBenefitApply = (
    benefitValues: Array<ObjectInfo>
  ) => {
    let fieldsToUpdate: Array<{ path: string, newValue: any }> = [];
    for (let val of benefitValues) {
      fieldsToUpdate.push(
        {
          path: val.benefitAppliedPath,
          newValue: val.value,
        },
      );
      if (!isDefined(getQuoteFieldValue(val.benefitDetailsPath))) {
        const benefitDetails = getBenefitDetailsObject(
          val.benefitDetailsObjectName,
          defaultPlanValues?.get('GAI7'),
          createACCBenefitStructure,
          planRootId,
          coverageDefId
        );
        fieldsToUpdate.push({
          path: val.benefitDetailsPath,
          newValue: fromJS(benefitDetails),
        });
      }
    }
    updateQuoteFieldValues(fieldsToUpdate);
  };

  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'treatment-support-recovery-units-row-1' },
      items: [
        {
          key: propertyPaths.unitsOfTreatmentSupportAndRecovery,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              options={unitsOptions}
              type="number"
              required
              disabled
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfTreatmentSupportAndRecovery)}
              id={propertyPaths.unitsOfTreatmentSupportAndRecovery}
            />
          ),
          props: { xs: 6, sm: 6, md: 6 },
        },
      ],
    },
  ];
  const TreatmentSupportAndRecovery: ConditionalSection[] = [
    {
      Component: CompanionNonLocalLodging,
      key: 'CompanionNonLocalLodging',
      label: 'COMPANION NON-LOCAL LODGING',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isCompanionNonLocalLodgingApplied),
    },
    {
      Component: MedicalEquipment,
      key: 'MedicalEquipment',
      label: 'MEDICAL EQUIPMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isMedicalEquipmentApplied),
    },
    {
      Component: MedicalSupplies,
      key: 'MedicalSupplies',
      label: 'MEDICAL SUPPLIES',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isMedicalSuppliesApplied),
    },
    {
      Component: Medication,
      key: 'Medication',
      label: 'MEDICATION',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isMedicationApplied),
    },
    {
      Component: NonLocalTransportation,
      key: 'NonLocalTransportation',
      label: 'NON-LOCAL TRANSPORTATION',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isNonLocalTransportationApplied),
    },
    {
      Component: PostAccidentCommonCarrierTransportation,
      key: 'PostAccidentCommonCarrierTransportation',
      label: 'POST-ACCIDENT COMMON CARRIER TRANSPORTATION',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isPostAccidentCommonCarrierTransportationApplied),
    },
    {
      Component: Prosthesis,
      key: 'Prosthesis',
      label: 'PROSTHESIS',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isProsthesisApplied),
    },
    {
      Component: ResidenceOrAutomobileModification,
      key: 'ResidenceOrAutomobileModification',
      label: 'RESIDENCE OR AUTOMOBILE MODIFICATION',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isResidenceAutomobileModificationApplied),
    },
    {
      Component: Therapy,
      key: 'Therapy',
      label: 'THERAPY',
      isApplied: situsStateCd === 'KS' ? true : getQuoteFieldValue<boolean>(propertyPaths.isTherapyApplied),
      disabled: true,
    },
    {
      Component: SpinalManipulationServicesDetails,
      key: 'spinalManipulationServicesDetails',
      label: 'SPINAL MANIPULATION SERVICES (KANSAS ONLY)',
      isApplied: situsStateCd === 'KS' ? true : false,
      disabled: true,
      hidden: situsStateCd !== 'KS' ? true : false,
      onToggleVisibility: (isApplied: boolean) => {
        onToggleBenefitApply([{ benefitAppliedPath: propertyPaths.isTherapyApplied, value: isApplied, benefitDetailsPath: propertyPaths.therapyDetails.therapyDetails, benefitDetailsObjectName: 'therapyDetails' },
        { benefitAppliedPath: propertyPaths.isSpinalManipulationServicesApplied, value: isApplied, benefitDetailsPath: propertyPaths.spinalManipulationServicesDetails.spinalManipulationServicesDetails, benefitDetailsObjectName: 'spinalManipulationServicesDetails' }]);
      },
    },
  ];

  return (
    <FormWrapper title="Treatment, Support and Recovery" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={TreatmentSupportAndRecovery} />
      </Grid>
    </FormWrapper>
  );
};
